import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_PYTHON_API_URL;

function audioUrl(filePath) {
  if (!filePath) return null;
  const filename = filePath.split("/").pop();
  return `${API_URL}/audio/${filename}`;
}

function PlayButton({ filePath, size = "md" }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const url = audioUrl(filePath);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  if (!url) return null;

  const toggle = (e) => {
    e.stopPropagation();
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const btnSize = size === "sm" ? "w-6 h-6" : "w-7 h-7";

  return (
    <button
      onClick={toggle}
      title={playing ? "Stop audio" : "Play audio"}
      className={`${btnSize} rounded-full flex items-center justify-center shrink-0 transition-all cursor-pointer active:scale-90`}
      style={
        playing
          ? { background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)", color: "#f87171" }
          : { background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.35)", color: "#22d3ee" }
      }
    >
      {playing ? (
        <svg className={iconSize} fill="currentColor" viewBox="0 0 24 24">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
      ) : (
        <svg className={iconSize} fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>
  );
}

const CASE_COLORS = {
  "Grocery Order": { bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.35)", text: "#4ade80" },
  "Patient Referral": { bg: "rgba(6,182,212,0.12)", border: "rgba(6,182,212,0.35)", text: "#22d3ee" },
  "Automotive Dealer Order": { bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.35)", text: "#c084fc" },
  "Pharma Order": { bg: "rgba(251,146,60,0.12)", border: "rgba(251,146,60,0.35)", text: "#fb923c" },
  "Middle-East Help Center": { bg: "rgba(244,63,94,0.12)", border: "rgba(244,63,94,0.35)", text: "#fb7185" },
};

function CaseBadge({ name }) {
  const c = CASE_COLORS[name] || { bg: "rgba(99,102,241,0.12)", border: "rgba(99,102,241,0.35)", text: "#818cf8" };
  return (
    <span
      className="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap"
      style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}
    >
      {name || "—"}
    </span>
  );
}

function JsonDrawer({ record, onClose }) {
  const [parsed, setParsed] = useState(null);
  const [raw, setRaw] = useState(false);
  const [displayLang, setDisplayLang] = useState("bn");

  const activeTranscription = displayLang === "bn"
    ? (record?.transcription ?? "—")
    : ((record?.en_transcription || record?.transcription) ?? "—");
  const activeJson = displayLang === "bn"
    ? (record?.json ?? null)
    : ((record?.en_json || record?.json) ?? null);

  useEffect(() => {
    if (!record) return;
    setDisplayLang("bn");
  }, [record]);

  useEffect(() => {
    if (!record) return;
    try {
      const jsonStr = displayLang === "bn" ? record?.json : (record?.en_json || record?.json);
      setParsed(typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr);
    } catch {
      setParsed(null);
    }
  }, [record, displayLang]);

  if (!record) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="relative h-full w-full max-w-lg flex flex-col overflow-hidden"
        style={{
          background: "rgba(2,6,23,0.97)",
          borderLeft: "1px solid rgba(6,182,212,0.2)",
          animation: "slideIn 0.25s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
        >
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-bold text-white">Record Details</h2>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={displayLang}
              onChange={(e) => setDisplayLang(e.target.value)}
              className="text-xs font-mono bg-cyan-500/20 text-cyan-400 px-3 py-1.5 rounded border border-cyan-500/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 hover:bg-cyan-500/30 hover:border-cyan-400/50 transition-colors cursor-pointer"
              aria-label="Select language"
            >
              <option value="bn" className="bg-slate-900 text-cyan-400">Bn</option>
              <option value="en" className="bg-slate-900 text-cyan-400">En</option>
            </select>
            <button
            onClick={onClose}
            className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Case + Date & Time — two columns in one row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Case</p>
              <CaseBadge name={record.case_name} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Date & Time</p>
              <p className="text-sm text-white font-mono">{record.date_time || "—"}</p>
            </div>
          </div>

          {/* Audio */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Audio File</p>
            <div className="flex items-center gap-2 w-fit">
              <span className="text-xs font-mono text-slate-400" title={record.audio_file}>
                {record.audio_file ? record.audio_file.split("/").pop() || record.audio_file : "—"}
              </span>
              {record.audio_file && <PlayButton filePath={record.audio_file} size="md" />}
            </div>
          </div>

          {/* Transcription */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Transcription</p>
            <div
              className="rounded-xl p-4 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              {activeTranscription}
            </div>
          </div>

          {/* JSON */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Extracted JSON</p>
              {parsed && activeJson && (
                <button
                  onClick={() => setRaw((r) => !r)}
                  className="text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300 transition-colors cursor-pointer"
                >
                  {raw ? "Formatted" : "Raw"}
                </button>
              )}
            </div>
            {!activeJson ? (
              <p className="text-slate-500 text-sm">No JSON data</p>
            ) : raw || !parsed ? (
              <pre
                className="text-xs text-slate-300 rounded-xl p-4 overflow-x-auto"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                {typeof activeJson === "string" ? activeJson : JSON.stringify(activeJson, null, 2)}
              </pre>
            ) : (() => {
              const ORDER_META = {
                "Grocery Order":           { icon: "🛒", color: "#22d3ee", label: "Order Items" },
                "Automotive Dealer Order": { icon: "🔧", color: "#a78bfa", label: "Parts Ordered" },
                "Pharma Order":            { icon: "💊", color: "#34d399", label: "Medicines" },
              };
              const meta = ORDER_META[record.case_name];
              const isOrderCase = !!meta && Array.isArray(parsed.items) && parsed.items.length > 0;

              if (isOrderCase) {
                const items = parsed.items;
                const nameKey = items[0]?.medicine_name !== undefined ? "medicine_name" : "product_name";
                const c = meta.color;
                return (
                  <div className="flex flex-col gap-3">
                    {/* item count badge */}
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">{meta.label}</span>
                      <span
                        className="text-[11px] font-bold px-3 py-0.5 rounded-full"
                        style={{ color: c, background: `${c}20`, border: `1px solid ${c}40` }}
                      >
                        {items.length} {items.length === 1 ? "item" : "items"}
                      </span>
                    </div>

                    {/* item rows */}
                    <div className="flex flex-col gap-2">
                      {items.map((item, i) => {
                        const name = item[nameKey] || item.product_name || item.medicine_name || "—";
                        const qty  = item.quantity || "";
                        const unit = item.unit || "";
                        const qtyDisplay = unit ? `${qty} ${unit}`.trim() : qty;
                        return (
                          <div
                            key={i}
                            className="relative flex items-center gap-3 rounded-2xl px-4 py-3 overflow-hidden"
                            style={{
                              background: `linear-gradient(135deg, ${c}12 0%, ${c}06 100%)`,
                              border: `1px solid ${c}30`,
                            }}
                          >
                            <div
                              className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl"
                              style={{ background: `linear-gradient(180deg, ${c}, ${c}60)` }}
                            />
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                              style={{ background: `${c}25`, color: c }}
                            >
                              {i + 1}
                            </div>
                            <span className="text-xl shrink-0">{meta.icon}</span>
                            <p className="flex-1 text-sm font-semibold leading-snug text-slate-100">{name}</p>
                            {(qty || unit) && (
                              <div
                                className="shrink-0 flex flex-col items-center justify-center rounded-xl px-3 py-1.5 min-w-[52px] text-center"
                                style={{ background: `${c}20`, border: `1px solid ${c}40` }}
                              >
                                <span className="text-[9px] uppercase tracking-widest text-slate-500 leading-none mb-0.5">qty</span>
                                <span className="text-sm font-bold leading-none" style={{ color: c }}>{qtyDisplay || "—"}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* summary footer */}
                    <div
                      className="flex items-center justify-center gap-2 rounded-xl py-2.5"
                      style={{ background: `${c}10`, border: `1px dashed ${c}30` }}
                    >
                      <span className="text-[11px] text-slate-500 uppercase tracking-widest">Total</span>
                      <span className="font-bold text-sm" style={{ color: c }}>
                        {items.length} {items.length === 1 ? "product" : "products"}
                      </span>
                    </div>
                  </div>
                );
              }

              /* fallback for non-order cases */
              return (
                <div
                  className="rounded-xl overflow-hidden"
                  style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  {Object.entries(parsed).map(([key, value]) => {
                    const isEmpty =
                      value === null || value === "" || (Array.isArray(value) && value.length === 0);
                    const displayKey = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                    return (
                      <div
                        key={key}
                        className="flex flex-col gap-1 px-4 py-3 border-b last:border-b-0"
                        style={{ borderColor: "rgba(255,255,255,0.06)" }}
                      >
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                          {displayKey}
                        </span>
                        {isEmpty ? (
                          <span className="text-slate-600 text-xs italic">—</span>
                        ) : Array.isArray(value) ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {value.map((item, i) => (
                              <li key={i} className="text-sm text-slate-200">
                                {typeof item === "object" ? JSON.stringify(item) : String(item)}
                              </li>
                            ))}
                          </ul>
                        ) : typeof value === "object" ? (
                          <div className="space-y-1">
                            {Object.entries(value).map(([k, v]) =>
                              v ? (
                                <div key={k} className="flex gap-2 text-sm">
                                  <span className="text-slate-500 capitalize min-w-[100px]">
                                    {k.replace(/_/g, " ")}:
                                  </span>
                                  <span className="text-slate-200">{String(v)}</span>
                                </div>
                              ) : null
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-200">{String(value)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default function RecordsView() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [caseFilter, setCaseFilter] = useState("All");
  const [displayLang, setDisplayLang] = useState("bn");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/records`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRecords(data.records || []);
    } catch (err) {
      setError("Failed to load records. Is the backend running?");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const deleteRecord = async (record) => {
    if (!window.confirm(`Delete this record?\n\nCase: ${record.case_name}\nDate: ${record.date_time}\n\nThis will also remove the audio file.`)) return;
    setDeletingId(record.id);
    try {
      const res = await fetch(`${API_URL}/records/${record.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRecords((prev) => prev.filter((r) => r.id !== record.id));
      if (selectedRecord?.id === record.id) setSelectedRecord(null);
    } catch (err) {
      alert("Failed to delete record. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const uniqueCases = ["All", ...new Set(records.map((r) => r.case_name).filter(Boolean))];

  const filtered = records.filter((r) => {
    const matchCase = caseFilter === "All" || r.case_name === caseFilter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (r.case_name || "").toLowerCase().includes(q) ||
      (r.transcription || "").toLowerCase().includes(q) ||
      (r.en_transcription || "").toLowerCase().includes(q) ||
      (r.date_time || "").toLowerCase().includes(q) ||
      (r.audio_file || "").toLowerCase().includes(q);
    return matchCase && matchSearch;
  });


  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #020617 0%, #0a1628 50%, #020617 100%)",
        color: "#e2e8f0",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {/* Ambient background blobs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div
          className="absolute top-[-10%] left-[-10%] w-96 h-96 rounded-full blur-[120px] opacity-30"
          style={{ background: "radial-gradient(circle, #0e7490, transparent)" }}
        />
        <div
          className="absolute bottom-[-10%] right-[-10%] w-96 h-96 rounded-full blur-[120px] opacity-20"
          style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/home")}
              className="p-2 rounded-full transition-colors cursor-pointer"
              style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.25)", color: "#22d3ee" }}
              title="Back to Home"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Records</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {process.env.REACT_APP_SITE_TITLE} · bn_solutions database
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={displayLang}
              onChange={(e) => setDisplayLang(e.target.value)}
              className="text-xs font-mono bg-cyan-500/20 text-cyan-400 px-3 py-1.5 rounded border border-cyan-500/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 hover:bg-cyan-500/30 hover:border-cyan-400/50 transition-colors cursor-pointer"
              aria-label="Select language"
            >
              <option value="bn" className="bg-slate-900 text-cyan-400">Bn</option>
              <option value="en" className="bg-slate-900 text-cyan-400">En</option>
            </select>
            <button
            onClick={fetchRecords}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer"
            style={{
              background: "rgba(6,182,212,0.1)",
              border: "1px solid rgba(6,182,212,0.25)",
              color: "#22d3ee",
            }}
          >
            <svg
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total Records", value: records.length, color: "#22d3ee" },
            { label: "Cases", value: uniqueCases.length - 1, color: "#c084fc" },
            { label: "Filtered", value: filtered.length, color: "#4ade80" },
            { label: "Shown", value: filtered.length, color: "#fb923c" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-xl px-4 py-3"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <p className="text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
              <p className="text-2xl font-bold mt-0.5" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search transcription, case, date…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#e2e8f0",
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {uniqueCases.map((c) => (
              <button
                key={c}
                onClick={() => setCaseFilter(c)}
                className="px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer"
                style={
                  caseFilter === c
                    ? { background: "rgba(6,182,212,0.2)", border: "1px solid rgba(6,182,212,0.5)", color: "#22d3ee" }
                    : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#94a3b8" }
                }
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
            <p className="text-slate-500 text-sm">Loading records…</p>
          </div>
        ) : error ? (
          <div
            className="rounded-2xl p-8 text-center"
            style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)" }}
          >
            <p className="text-red-400 font-semibold">{error}</p>
            <button
              onClick={fetchRecords}
              className="mt-4 px-4 py-2 rounded-xl bg-red-500/20 text-red-400 text-sm cursor-pointer hover:bg-red-500/30 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="rounded-2xl p-16 text-center"
            style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <svg className="w-12 h-12 text-slate-700 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-slate-500 text-sm">No records found.</p>
          </div>
        ) : (
          <div
            className="rounded-2xl overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.07)" }}
          >
            {/* Table header — sticky so it stays visible while scrolling */}
            <div
              className="grid text-[10px] uppercase tracking-widest font-semibold text-slate-500 px-4 py-3 sticky top-0 z-10"
              style={{
                background: "rgba(10,22,40,0.97)",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                gridTemplateColumns: "1fr 1.4fr 180px 80px 60px",
                gap: "1rem",
              }}
            >
              <span>Case</span>
              <span>Transcription</span>
              <span className="pl-4">Audio File</span>
              <span className="text-center">Details</span>
              <span className="text-center">Delete</span>
            </div>

            {/* Rows — scrollable */}
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {filtered.map((record, idx) => {
                const hasJson = !!record.json;
                const audioFilename = record.audio_file
                  ? record.audio_file.split("/").pop() || record.audio_file
                  : null;
                return (
                  <div
                    key={record.id}
                    className="group grid px-4 py-4 items-start transition-colors hover:bg-white/[0.02]"
                    style={{
                      gridTemplateColumns: "1fr 1.4fr 180px 80px 60px",
                      gap: "1rem",
                      borderBottom: idx < filtered.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                      background: selectedRecord?.id === record.id ? "rgba(6,182,212,0.05)" : undefined,
                    }}
                  >
                    {/* Case + Date & Time stacked */}
                    <div className="flex flex-col gap-1 pt-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CaseBadge name={record.case_name} />
                        <span className="text-[10px] font-mono text-slate-500 whitespace-nowrap">
                          {record.date_time || "—"}
                        </span>
                      </div>
                    </div>

                    {/* Transcription — 2 lines, ellipsis, no expand */}
                    <div className="pt-0.5">
                      <p
                        className="text-sm text-slate-300 leading-relaxed"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {displayLang === "bn"
                          ? (record.transcription || "—")
                          : (record.en_transcription || record.transcription || "—")}
                      </p>
                    </div>

                    {/* Audio file — filename only + play button tight together */}
                    <div
                      className="flex items-center gap-2 pt-0.5 pl-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span
                        className="text-xs font-mono text-slate-500 whitespace-nowrap"
                        title={record.audio_file}
                      >
                        {audioFilename || "—"}
                      </span>
                      {record.audio_file && <PlayButton filePath={record.audio_file} size="sm" />}
                    </div>

                    {/* Details button */}
                    <div className="flex items-start justify-center pt-0.5">
                      {(hasJson || record.transcription || record.en_transcription) ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedRecord(record); }}
                          className="text-[10px] font-semibold px-2 py-1 rounded-lg transition-all cursor-pointer hover:opacity-80"
                          style={{
                            background: "rgba(6,182,212,0.1)",
                            border: "1px solid rgba(6,182,212,0.3)",
                            color: "#22d3ee",
                          }}
                        >
                          Details
                        </button>
                      ) : (
                        <span className="text-slate-700 text-[10px]">—</span>
                      )}
                    </div>

                    {/* Delete button */}
                    <div className="flex items-start justify-center pt-0.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteRecord(record); }}
                        disabled={deletingId === record.id}
                        title="Delete record"
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{
                          background: "rgba(239,68,68,0.1)",
                          border: "1px solid rgba(239,68,68,0.3)",
                          color: "#f87171",
                        }}
                      >
                        {deletingId === record.id ? (
                          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      <JsonDrawer record={selectedRecord} onClose={() => setSelectedRecord(null)} />
    </div>
  );
}
